import React, { useState, useEffect, useRef } from "react";
import apiClient from "../api/client";
import ChipInput from "../components/ChipInput";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from "recharts";

// ── Colours ───────────────────────────────────────────────────────────────────
const NAVY  = "#1B3A5C";
const TEAL  = "#0D7377";
const AMBER = "#F0A500";
const SLATE = "#94A3B8";

// ── Sub-tab list ──────────────────────────────────────────────────────────────
const TABS = [
  { id: "summary",   label: "Monthly Summary" },
  { id: "providers", label: "Provider Costs" },
  { id: "mg",        label: "MG Tracker" },
  { id: "sports",    label: "Sport & League" },
  { id: "cost-comparison", label: "Cost Comparison" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(val, decimals = 0) {
  if (val == null) return "—";
  return Number(val).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtPct(val) {
  if (val == null) return "—";
  return `${Number(val).toFixed(2)}%`;
}

// Truncates a long category label (e.g. a contract name) for chart axes —
// the full value is still available via the tooltip on hover.
function truncateLabel(label, max = 24) {
  if (!label) return label;
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// Picks the field for the active metric view. Standard and Theo often share a
// column (e.g. turnover has no separate "theo" variant) — pass the same key
// twice in that case.
function metricValue(row, view, standardKey, theoKey, weightedKey) {
  const key = view === "theo" ? theoKey : view === "weighted" ? weightedKey : standardKey;
  return row[key];
}

const METRIC_VIEWS = [
  { id: "standard", label: "Standard" },
  { id: "theo",     label: "Theo" },
  { id: "weighted", label: "Weighted" },
];

// Monthly Summary chart-type toggle
const CHART_VIEWS = [
  { id: "ggr_vs_net",     label: "GGR vs Net GGR" },
  { id: "margins",        label: "Gross vs Net Margin %" },
  { id: "cost_breakdown", label: "Cost Breakdown" },
];

function MetricViewToggle({ value, onChange, disabled }) {
  return (
    <div title={disabled ? "Not used on Cost Comparison — it already shows Standard, Theo, and Weighted side by side" : undefined}>
      <label className="block text-xs text-gray-500 font-medium mb-1">Metric View</label>
      <div className={`flex bg-white border border-gray-300 rounded-lg overflow-hidden ${disabled ? "opacity-40" : ""}`}>
        {METRIC_VIEWS.map(v => (
          <button
            key={v.id}
            onClick={() => !disabled && onChange(v.id)}
            disabled={disabled}
            className="px-3 py-1.5 text-sm font-medium transition-colors"
            style={value === v.id && !disabled ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280", cursor: disabled ? "not-allowed" : "pointer" }}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Currency conversion ───────────────────────────────────────────────────────
// Applies to Insights only, not Finance — Finance reconciles actual invoices
// against their real payment currency, so converting those would work against
// the point of that tab.
const CURRENCIES = ["GBP", "EUR", "USD"];
const CURRENCY_SYMBOLS = { GBP: "£", EUR: "€", USD: "$" };

// fxRows: [{ month, currency_code, gbp_rate }] from /api/ref/fx-rates.
// gbp_rate = units of that currency per 1 GBP. Returns { "YYYY-MM": { CCY: rate } }.
function buildRatesByMonth(fxRows) {
  const map = {};
  (fxRows || []).forEach(r => {
    const m = (r.month?.value || r.month)?.slice(0, 7);
    if (!m) return;
    if (!map[m]) map[m] = {};
    map[m][r.currency_code] = r.gbp_rate;
  });
  return map;
}

// Converts a single-row money value. Must run before summing across months —
// rates move month to month, so an already-summed multi-month total can't be
// converted correctly with one point-in-time rate. Falls back to the raw
// amount if a rate is missing rather than silently dropping the value.
function convertAmount(amount, fromCurrency, monthKey, toCurrency, ratesByMonth) {
  if (amount == null || !fromCurrency || fromCurrency === toCurrency) return amount;
  const monthRates = ratesByMonth?.[monthKey];
  const fromRate = monthRates?.[fromCurrency];
  const toRate   = monthRates?.[toCurrency];
  if (!fromRate || !toRate) return amount;
  return amount * (toRate / fromRate);
}

function CurrencyToggle({ value, onChange, disabled }) {
  return (
    <div title={disabled ? "Not available on this tab yet" : undefined}>
      <label className="block text-xs text-gray-500 font-medium mb-1">Currency</label>
      <div className={`flex bg-white border border-gray-300 rounded-lg overflow-hidden ${disabled ? "opacity-40" : ""}`}>
        {CURRENCIES.map(c => (
          <button
            key={c}
            onClick={() => !disabled && onChange(c)}
            disabled={disabled}
            className="px-3 py-1.5 text-sm font-medium transition-colors"
            style={value === c && !disabled ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280", cursor: disabled ? "not-allowed" : "pointer" }}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── CSV export helper ─────────────────────────────────────────────────────────
function csvEscape(val) {
  const s = val == null ? "" : String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, headers, rows) {
  const lines = [headers, ...rows].map(r => r.map(csvEscape).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color: NAVY }}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="w-8 h-8 border-4 border-gray-200 rounded-full animate-spin"
        style={{ borderTopColor: TEAL }} />
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
      {message}
    </div>
  );
}

// ── Multi-select dropdown component ──────────────────────────────────────────
function MultiSelect({ label, options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const toggleOption = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter(s => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  const displayLabel = selected.length === 0
    ? (placeholder || `All ${label}s`)
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs text-gray-500 font-medium mb-1">{label}</label>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white min-w-40 focus:outline-none focus:ring-2 focus:ring-teal-500"
        style={{ color: selected.length ? NAVY : "#9ca3af" }}
      >
        <span className="truncate max-w-36">{displayLabel}</span>
        <span className="text-gray-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg min-w-48 max-h-64 overflow-y-auto">
          {/* Clear all */}
          {selected.length > 0 && (
            <button
              onClick={() => { onChange([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 border-b border-gray-100"
            >
              Clear all
            </button>
          )}
          {options.map(opt => (
            <label key={opt} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggleOption(opt)}
                className="rounded"
                style={{ accentColor: TEAL }}
              />
              <span className="text-gray-700">{opt}</span>
            </label>
          ))}
          {options.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-400">No options available</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Apply button ──────────────────────────────────────────────────────────────
function ApplyButton({ onClick, dirty }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-colors mt-4"
      style={{ backgroundColor: dirty ? TEAL : "#94a3b8", cursor: dirty ? "pointer" : "default" }}
    >
      Apply
    </button>
  );
}

// ── Global filter bar ─────────────────────────────────────────────────────────
// Holds pending state locally, only fires onChange when Apply is clicked
function FilterBar({
  filters, onChange, onExport,
  metricView, onMetricViewChange, metricViewDisabled,
  currency, onCurrencyChange, currencyDisabled,
}) {
  const [pending, setPending] = useState(filters);
  const dirty = JSON.stringify(pending) !== JSON.stringify(filters);

  // Sync if parent resets
  useEffect(() => { setPending(filters); }, [filters]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">From</label>
            <input
              type="month"
              value={pending.startMonth}
              onChange={e => setPending(p => ({ ...p, startMonth: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="mt-4">→</div>
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">To</label>
            <input
              type="month"
              value={pending.endMonth}
              onChange={e => setPending(p => ({ ...p, endMonth: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        <MultiSelect
          label="Brand"
          options={["BrandUK", "BrandIE"]}
          selected={pending.brands}
          onChange={v => setPending(p => ({ ...p, brands: v }))}
          placeholder="All Brands"
        />

        {onMetricViewChange && (
          <MetricViewToggle value={metricView} onChange={onMetricViewChange} disabled={metricViewDisabled} />
        )}

        {onCurrencyChange && (
          <CurrencyToggle value={currency} onChange={onCurrencyChange} disabled={currencyDisabled} />
        )}

        <div className="flex items-end gap-2">
          <ApplyButton onClick={() => onChange(pending)} dirty={dirty} />
          {(filters.startMonth || filters.endMonth || filters.brands.length > 0) && (
            <button
              onClick={() => { const r = { startMonth: "", endMonth: "", brands: [] }; setPending(r); onChange(r); }}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-700 mt-4"
            >
              Reset
            </button>
          )}
        </div>

        {onExport && (
          <div className="flex items-end ml-auto">
            <button
              onClick={onExport}
              className="px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors mt-4"
              style={{ borderColor: TEAL, color: TEAL }}
              title="Export the current tab's filtered results as CSV"
            >
              Export CSV
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab filter bar wrapper ────────────────────────────────────────────────────
function TabFilterBar({ children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
      <div className="flex items-end gap-4 flex-wrap">
        {children}
      </div>
    </div>
  );
}

// ── 1. Monthly Summary ────────────────────────────────────────────────────────
function MonthlySummary({ filters, tabId, exportRegistry, metricView, currency, ratesByMonth }) {
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [aggregate, setAggregate] = useState(false);
  const [chartView, setChartView] = useState("ggr_vs_net");

  // Always fetch monthly-granularity rows — currency conversion has to happen
  // per row before any summing, since the rate moves month to month. So
  // "Aggregated" is computed client-side below rather than asked of BigQuery.
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.startMonth)     params.start_month = filters.startMonth + "-01";
    if (filters.endMonth)       params.end_month   = filters.endMonth   + "-01";
    // For single brand filter — if multiple selected, send first (API supports one brand at a time)
    // Multi-brand filtering is done client-side from the full dataset
    if (filters.brands?.length === 1) params.brand = filters.brands[0];

    apiClient.get("/api/insights/summary", { params })
      .then(res => {
        let d = res.data;
        // Client-side multi-brand filter
        if (filters.brands?.length > 1) {
          d = d.filter(r => filters.brands.includes(r.brand_label));
        }
        setData(d);
        setError(null);
      })
      .catch(() => setError("Failed to load monthly summary."))
      .finally(() => setLoading(false));
  }, [filters]);

  const headers = aggregate
    ? ["Brand","Currency","Turnover","GGR","Post-Tax GGR","Total Cost","Net GGR","Rev Share %"]
    : ["Month","Brand","Currency","Turnover","GGR","Post-Tax GGR","Total Cost","Net GGR","Rev Share %"];

  const symbol = CURRENCY_SYMBOLS[currency] || currency || "£";

  function conv(amount, row) {
    if (!currency) return amount;
    const monthKey = (row.month?.value || row.month)?.slice(0, 7);
    return convertAmount(amount, row.currency_code, monthKey, currency, ratesByMonth);
  }
  const turnoverOf     = row => conv(metricValue(row, metricView, "total_turnover", "total_turnover", "total_turnover_weighted"), row);
  const ggrOf          = row => conv(metricValue(row, metricView, "total_ggr", "total_theo_ggr", "total_ggr_weighted"), row);
  const postTaxOf      = row => conv(metricValue(row, metricView, "total_post_tax_ggr", "total_post_tax_theo_ggr", "total_post_tax_ggr_weighted"), row);
  const costOf         = row => conv(metricValue(row, metricView, "total_effective_cost", "total_effective_cost_theo", "total_effective_cost_weighted"), row);
  const taxCostOf      = row => conv(metricValue(row, metricView, "total_tax_cost", "total_tax_cost_theo", "total_tax_cost_weighted"), row);
  const providerCostOf = row => conv(metricValue(row, metricView, "total_provider_cost", "total_provider_cost_theo", "total_provider_cost_weighted"), row);
  const netGgrOf       = row => conv(metricValue(row, metricView, "total_post_cost_ggr", "total_post_cost_theo_ggr", "total_post_cost_ggr_weighted"), row);
  // Rev share % is a same-currency ratio (cost / GGR) so it doesn't need converting
  const revShareOf     = row => metricValue(row, metricView, "effective_rev_share_pct", "effective_rev_share_theo_pct", "effective_rev_share_weighted_pct");

  // Groups rows by an arbitrary key (month, or brand when aggregated) and sums
  // every $ metric, then recomputes margins/rev-share from those sums rather
  // than blending precomputed per-row percentages — needed both when
  // aggregating across months AND when a month bucket blends multiple brands
  // together, since either case can mix rows a plain percentage average would
  // get wrong.
  function buildGroups(rows, keyFn, keyField) {
    const groups = {};
    rows.forEach(row => {
      const k = keyFn(row);
      if (!groups[k]) groups[k] = {
        [keyField]: k, turnover: 0, ggr: 0, postTax: 0, cost: 0, net: 0, taxCost: 0, providerCost: 0,
      };
      const g = groups[k];
      g.turnover     += turnoverOf(row)     || 0;
      g.ggr          += ggrOf(row)          || 0;
      g.postTax      += postTaxOf(row)      || 0;
      g.cost         += costOf(row)         || 0;
      g.net          += netGgrOf(row)       || 0;
      g.taxCost      += taxCostOf(row)      || 0;
      g.providerCost += providerCostOf(row) || 0;
    });
    return Object.values(groups).map(g => ({
      ...g,
      grossMargin: g.turnover ? (g.ggr / g.turnover) * 100 : null,
      netMargin:   g.turnover ? (g.net / g.turnover) * 100 : null,
      revShare:    g.ggr      ? (g.cost / g.ggr) * 100      : null,
    }));
  }

  // Client-side aggregation by brand — used by the Aggregated table, its
  // chart, and its CSV export.
  const aggregatedRows = buildGroups(data, row => row.brand_label, "brand")
    .sort((a, b) => b.ggr - a.ggr);

  if (exportRegistry) {
    exportRegistry.current[tabId] = () => {
      const rows = aggregate
        ? aggregatedRows.map(row => [
            row.brand, currency,
            fmt(row.turnover), fmt(row.ggr), fmt(row.postTax), fmt(row.cost), fmt(row.net), fmtPct(row.revShare),
          ])
        : data.map(row => {
            const month = (row.month?.value || row.month)?.slice(0, 7);
            return [
              month, row.brand_label, currency,
              fmt(turnoverOf(row)), fmt(ggrOf(row)), fmt(postTaxOf(row)),
              fmt(costOf(row)), fmt(netGgrOf(row)), fmtPct(revShareOf(row)),
            ];
          });
      downloadCsv(`insights-monthly-summary-${metricView}-${currency}-${todayStr()}.csv`, headers, rows);
    };
  }

  if (loading) return <LoadingSpinner />;
  if (error)   return <ErrorBox message={error} />;
  if (!data.length) return <p className="text-gray-400 text-sm">No data found for selected filters.</p>;

  const totals = data.reduce((acc, row) => ({
    turnover:      (acc.turnover      || 0) + (turnoverOf(row) || 0),
    ggr:           (acc.ggr           || 0) + (ggrOf(row)      || 0),
    cost:          (acc.cost          || 0) + (costOf(row)     || 0),
    post_cost_ggr: (acc.post_cost_ggr || 0) + (netGgrOf(row)   || 0),
  }), {});

  const blendedRevShare = totals.ggr
    ? ((totals.cost / totals.ggr) * 100).toFixed(2)
    : null;

  // Chart grouping: Monthly blends every row into one bucket per month
  // regardless of brand. Aggregated now does the same but for the whole
  // filtered range — collapsing to a single total rather than splitting by
  // brand, since currency conversion already unifies brands into one figure
  // and the global Brand filter is there if you want to isolate one.
  const aggregateLabel = filters.brands?.length === 1 ? filters.brands[0] : "All Brands";
  const chartData = aggregate
    ? buildGroups(data, () => aggregateLabel, "group")
    : buildGroups(data, row => (row.month?.value || row.month)?.slice(0, 7), "month")
        .sort((a, b) => a.month.localeCompare(b.month));
  const chartXKey = aggregate ? "group" : "month";

  const chartTitle = aggregate
    ? {
        ggr_vs_net:     "GGR vs Net GGR (Aggregated)",
        margins:        "Gross vs Net Margin % (Aggregated)",
        cost_breakdown: "Cost Breakdown vs GGR (Aggregated)",
      }[chartView]
    : {
        ggr_vs_net:     "GGR vs Net GGR by Month",
        margins:        "Gross vs Net Margin % by Month",
        cost_breakdown: "Cost Breakdown vs GGR by Month",
      }[chartView];

  return (
    <div className="space-y-6">
      {/* Aggregation toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">View:</span>
        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <button
            onClick={() => setAggregate(false)}
            className="px-4 py-1.5 text-sm font-medium transition-colors"
            style={!aggregate ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            Monthly
          </button>
          <button
            onClick={() => setAggregate(true)}
            className="px-4 py-1.5 text-sm font-medium transition-colors"
            style={aggregate ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            Aggregated
          </button>
        </div>
        <span className="text-xs text-gray-400">
          {aggregate ? "Summed across all months in filter (Rev Share % recomputed from totals)" : "One row per month"}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total Turnover"      value={`${symbol}${fmt(totals.turnover)}`} />
        <KpiCard label="Total GGR"           value={`${symbol}${fmt(totals.ggr)}`} />
        <KpiCard label="Total Cost"          value={`${symbol}${fmt(totals.cost)}`} sub="Tax + Provider" />
        <KpiCard label="Effective Rev Share" value={fmtPct(blendedRevShare)} sub="cost / GGR" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-semibold" style={{ color: NAVY }}>{chartTitle}</h3>
          <div className="flex bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
            {CHART_VIEWS.map(v => (
              <button
                key={v.id}
                onClick={() => setChartView(v.id)}
                className="px-3 py-1.5 text-xs font-medium transition-colors"
                style={chartView === v.id ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          {chartView === "margins" ? (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={chartXKey} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${Number(v).toFixed(0)}%`} />
              <Tooltip formatter={(v, name) => [`${Number(v).toFixed(2)}%`, name]} />
              <Legend />
              <Bar dataKey="grossMargin" fill={TEAL} name="Gross Margin %" radius={[4,4,0,0]} />
              <Bar dataKey="netMargin"   fill={NAVY} name="Net Margin %"   radius={[4,4,0,0]} />
            </BarChart>
          ) : chartView === "cost_breakdown" ? (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={chartXKey} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${symbol}${fmt(v)}`} />
              <Tooltip formatter={(v, name) => [`${symbol}${fmt(v)}`, name]} />
              <Legend />
              <Bar dataKey="ggr"          fill={TEAL}    name="GGR"  radius={[4,4,0,0]} />
              <Bar dataKey="taxCost"      stackId="cost" fill={SLATE} name="Tax Cost" />
              <Bar dataKey="providerCost" stackId="cost" fill={AMBER} name="Provider Cost" radius={[4,4,0,0]} />
            </BarChart>
          ) : (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={chartXKey} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${symbol}${fmt(v)}`} />
              <Tooltip formatter={(v, name) => [`${symbol}${fmt(v)}`, name]} />
              <Legend />
              <Bar dataKey="ggr" fill={TEAL} name="GGR"     radius={[4,4,0,0]} />
              <Bar dataKey="net" fill={NAVY} name="Net GGR" radius={[4,4,0,0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {headers.map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aggregate
              ? aggregatedRows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-4 py-2.5 font-medium">{row.brand}</td>
                    <td className="px-4 py-2.5">{currency}</td>
                    <td className="px-4 py-2.5">{fmt(row.turnover)}</td>
                    <td className="px-4 py-2.5">{fmt(row.ggr)}</td>
                    <td className="px-4 py-2.5">{fmt(row.postTax)}</td>
                    <td className="px-4 py-2.5">{fmt(row.cost)}</td>
                    <td className="px-4 py-2.5">{fmt(row.net)}</td>
                    <td className="px-4 py-2.5">{fmtPct(row.revShare)}</td>
                  </tr>
                ))
              : data.map((row, i) => {
                  const month = (row.month?.value || row.month)?.slice(0, 7);
                  return (
                    <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                      <td className="px-4 py-2.5 font-medium">{month}</td>
                      <td className="px-4 py-2.5">{row.brand_label}</td>
                      <td className="px-4 py-2.5">{currency || row.currency_code}</td>
                      <td className="px-4 py-2.5">{fmt(turnoverOf(row))}</td>
                      <td className="px-4 py-2.5">{fmt(ggrOf(row))}</td>
                      <td className="px-4 py-2.5">{fmt(postTaxOf(row))}</td>
                      <td className="px-4 py-2.5">{fmt(costOf(row))}</td>
                      <td className="px-4 py-2.5">{fmt(netGgrOf(row))}</td>
                      <td className="px-4 py-2.5">{fmtPct(revShareOf(row))}</td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 2. Provider Costs ─────────────────────────────────────────────────────────
const PROVIDER_COSTS_TABLE_LIMIT = 100;

function ProviderCosts({ filters, tabId, exportRegistry, metricView, currency, ratesByMonth }) {
  const [data,             setData]             = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [aggregate,        setAggregate]        = useState(false);
  const [chartView,        setChartView]        = useState("cost");
  const [contractOptions,  setContractOptions]  = useState([]);
  const [costTypeOptions]                       = useState([
    "Data","Data & Streaming","Streaming","Tax","Levy","Match Tracker","Virtual Games","Web Content"
  ]);

  // Pending (uncommitted) tab filter state
  const [pendingContracts, setPendingContracts] = useState([]);
  const [pendingCostTypes, setPendingCostTypes] = useState([]);

  // Applied (committed) tab filter state — only changes on Apply
  const [appliedContracts, setAppliedContracts] = useState([]);
  const [appliedCostTypes, setAppliedCostTypes] = useState([]);

  const tabDirty =
    JSON.stringify(pendingContracts) !== JSON.stringify(appliedContracts) ||
    JSON.stringify(pendingCostTypes) !== JSON.stringify(appliedCostTypes);

  // Fetch contract name options once on mount
  useEffect(() => {
    apiClient.get("/api/contracts/")
      .then(res => {
        const names = [...new Set(res.data.map(r => r.contract_name))].sort();
        setContractOptions(names);
      })
      .catch(() => {});
  }, []);

  // Fetch data when global filters or applied tab filters change. Always
  // fetches monthly-granularity rows — currency conversion must happen per
  // row before summing, so "Aggregated" is computed client-side below.
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.startMonth)        params.start_month = filters.startMonth + "-01";
    if (filters.endMonth)          params.end_month   = filters.endMonth   + "-01";
    if (filters.brands?.length === 1) params.brand    = filters.brands[0];

    apiClient.get("/api/insights/providers", { params })
      .then(res => {
        let d = res.data;
        // Client-side filtering for multi-selects
        if (filters.brands?.length > 1)    d = d.filter(r => filters.brands.includes(r.brand_label));
        if (appliedContracts.length > 0)   d = d.filter(r => appliedContracts.includes(r.contract_name));
        if (appliedCostTypes.length > 0)   d = d.filter(r => appliedCostTypes.includes(r.cost_type));
        setData(d);
        setError(null);
      })
      .catch(() => setError("Failed to load provider costs."))
      .finally(() => setLoading(false));
  }, [filters, appliedContracts, appliedCostTypes]);

  function handleApply() {
    setAppliedContracts(pendingContracts);
    setAppliedCostTypes(pendingCostTypes);
  }

  function handleReset() {
    setPendingContracts([]);
    setPendingCostTypes([]);
    setAppliedContracts([]);
    setAppliedCostTypes([]);
  }

  const symbol = CURRENCY_SYMBOLS[currency] || currency || "£";

  function conv(amount, row) {
    if (!currency) return amount;
    const monthKey = (row.month?.value || row.month)?.slice(0, 7);
    return convertAmount(amount, row.currency_code, monthKey, currency, ratesByMonth);
  }
  const ggrOf      = row => conv(metricValue(row, metricView, "total_rel_ggr", "total_rel_theo_ggr", "total_rel_ggr_weighted"), row);
  const costOf     = row => conv(metricValue(row, metricView, "total_effective_cost", "total_effective_cost_theo", "total_effective_cost_weighted"), row);
  // Rev share % is a same-currency ratio (cost / GGR) so it doesn't need converting
  const revShareOf = row => metricValue(row, metricView, "effective_rev_share_pct", "effective_rev_share_theo_pct", "effective_rev_share_weighted_pct");

  const headers = aggregate
    ? ["Contract","Cost Type","Brand","Currency","GGR","Effective Cost","MG Applied","Rev Share %"]
    : ["Month","Contract","Cost Type","Brand","Currency","GGR","Effective Cost","MG Applied","Rev Share %"];

  const byContract = {};
  (data || []).forEach(row => {
    const k = row.contract_name;
    if (!byContract[k]) byContract[k] = { contract: k, cost: 0, ggr: 0, mg: false };
    byContract[k].cost += costOf(row) || 0;
    byContract[k].ggr  += ggrOf(row)  || 0;
    if (row.mg_applied) byContract[k].mg = true;
  });
  const costChartData = Object.values(byContract).sort((a, b) => b.cost - a.cost).slice(0, 15);
  const revShareChartData = Object.values(byContract)
    .map(c => ({ contract: c.contract, revShare: c.ggr ? (c.cost / c.ggr) * 100 : null }))
    .filter(c => c.revShare != null)
    .sort((a, b) => b.revShare - a.revShare)
    .slice(0, 15);
  const chartData = chartView === "cost" ? costChartData : revShareChartData;

  // Client-side aggregation (contract + cost type + brand) — mirrors the
  // backend's aggregate query but runs after currency conversion, since a
  // pre-summed multi-month total can't be converted correctly afterwards.
  const byGroup = {};
  (data || []).forEach(row => {
    const key = `${row.contract_name}|${row.cost_type}|${row.brand_label}`;
    if (!byGroup[key]) byGroup[key] = {
      contract_name: row.contract_name, cost_type: row.cost_type, brand_label: row.brand_label,
      ggr: 0, cost: 0, mg_applied: false,
    };
    byGroup[key].ggr  += ggrOf(row)  || 0;
    byGroup[key].cost += costOf(row) || 0;
    if (row.mg_applied) byGroup[key].mg_applied = true;
  });
  const aggregatedRows = Object.values(byGroup).map(g => ({
    ...g,
    revShare: g.ggr ? (g.cost / g.ggr) * 100 : null,
  }));

  // Sorted by the active view's Effective Cost descending — drives both the display cap and the CSV export
  const sortedRows = aggregate
    ? [...aggregatedRows].sort((a, b) => (b.cost || 0) - (a.cost || 0))
    : [...data].sort((a, b) => (costOf(b) || 0) - (costOf(a) || 0));
  const tableRows = sortedRows.slice(0, PROVIDER_COSTS_TABLE_LIMIT);

  if (exportRegistry) {
    exportRegistry.current[tabId] = () => {
      const rows = aggregate
        ? sortedRows.map(row => [
            row.contract_name, row.cost_type, row.brand_label, currency,
            fmt(row.ggr), fmt(row.cost), row.mg_applied ? "Yes" : "No", fmtPct(row.revShare),
          ])
        : sortedRows.map(row => {
            const month = (row.month?.value || row.month)?.slice(0, 7);
            return [
              month, row.contract_name, row.cost_type, row.brand_label, currency,
              fmt(ggrOf(row)), fmt(costOf(row)),
              row.mg_applied ? "Yes" : "No", fmtPct(revShareOf(row)),
            ];
          });
      downloadCsv(`insights-provider-costs-${metricView}-${currency}-${todayStr()}.csv`, headers, rows);
    };
  }

  return (
    <div className="space-y-6">
      <TabFilterBar>
        <MultiSelect
          label="Contract"
          options={contractOptions}
          selected={pendingContracts}
          onChange={setPendingContracts}
          placeholder="All Contracts"
        />
        <MultiSelect
          label="Cost Type"
          options={costTypeOptions}
          selected={pendingCostTypes}
          onChange={setPendingCostTypes}
          placeholder="All Cost Types"
        />
        <div className="flex items-end gap-2">
          <ApplyButton onClick={handleApply} dirty={tabDirty} />
          {(appliedContracts.length > 0 || appliedCostTypes.length > 0) && (
            <button onClick={handleReset} className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-700 mt-4">
              Reset
            </button>
          )}
        </div>
      </TabFilterBar>

      {/* Aggregation toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">View:</span>
        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <button
            onClick={() => setAggregate(false)}
            className="px-4 py-1.5 text-sm font-medium transition-colors"
            style={!aggregate ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            Monthly
          </button>
          <button
            onClick={() => setAggregate(true)}
            className="px-4 py-1.5 text-sm font-medium transition-colors"
            style={aggregate ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            Aggregated
          </button>
        </div>
        <span className="text-xs text-gray-400">
          {aggregate ? "Summed across all months in filter (Rev Share % based on GGR)" : "One row per month"}
        </span>
      </div>

      {loading ? <LoadingSpinner /> : error ? <ErrorBox message={error} /> : !data.length ? (
        <p className="text-gray-400 text-sm">No data found.</p>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-sm font-semibold" style={{ color: NAVY }}>
                {chartView === "cost" ? "Effective Cost by Contract (top 15)" : "Rev Share % by Contract (top 15)"}
              </h3>
              <div className="flex bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setChartView("cost")}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={chartView === "cost" ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
                >
                  Cost
                </button>
                <button
                  onClick={() => setChartView("rev_share")}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={chartView === "rev_share" ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
                >
                  Rev Share %
                </button>
              </div>
            </div>
            {/* Height scales with row count, and interval={0} forces every label to
                render — otherwise Recharts silently skips ticks once labels start
                crowding, which was hiding every other contract name. */}
            <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 32)}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                {chartView === "cost" ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `${symbol}${fmt(v)}`} />
                    <YAxis type="category" dataKey="contract" tick={{ fontSize: 10 }} width={190} interval={0} tickFormatter={v => truncateLabel(v)} />
                    <Tooltip formatter={v => `${symbol}${fmt(v)}`} labelFormatter={l => l} />
                    <Bar dataKey="cost" fill={TEAL} name="Effective Cost" radius={[0,4,4,0]} />
                  </>
                ) : (
                  <>
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `${Number(v).toFixed(0)}%`} />
                    <YAxis type="category" dataKey="contract" tick={{ fontSize: 10 }} width={190} interval={0} tickFormatter={v => truncateLabel(v)} />
                    <Tooltip formatter={v => `${Number(v).toFixed(2)}%`} labelFormatter={l => l} />
                    <Bar dataKey="revShare" fill={AMBER} name="Rev Share %" radius={[0,4,4,0]} />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {headers.map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aggregate
                  ? tableRows.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                        <td className="px-4 py-2.5 font-medium">{row.contract_name}</td>
                        <td className="px-4 py-2.5">{row.cost_type}</td>
                        <td className="px-4 py-2.5">{row.brand_label}</td>
                        <td className="px-4 py-2.5">{currency}</td>
                        <td className="px-4 py-2.5">{fmt(row.ggr)}</td>
                        <td className="px-4 py-2.5">{fmt(row.cost)}</td>
                        <td className="px-4 py-2.5">
                          {row.mg_applied
                            ? <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}>Yes</span>
                            : <span className="text-xs text-gray-400">No</span>}
                        </td>
                        <td className="px-4 py-2.5">{fmtPct(row.revShare)}</td>
                      </tr>
                    ))
                  : tableRows.map((row, i) => {
                      const month = (row.month?.value || row.month)?.slice(0, 7);
                      return (
                        <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                          <td className="px-4 py-2.5">{month}</td>
                          <td className="px-4 py-2.5 font-medium">{row.contract_name}</td>
                          <td className="px-4 py-2.5">{row.cost_type}</td>
                          <td className="px-4 py-2.5">{row.brand_label}</td>
                          <td className="px-4 py-2.5">{currency || row.currency_code}</td>
                          <td className="px-4 py-2.5">{fmt(ggrOf(row))}</td>
                          <td className="px-4 py-2.5">{fmt(costOf(row))}</td>
                          <td className="px-4 py-2.5">
                            {row.mg_applied
                              ? <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}>Yes</span>
                              : <span className="text-xs text-gray-400">No</span>}
                          </td>
                          <td className="px-4 py-2.5">{fmtPct(revShareOf(row))}</td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
            <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
              Showing top {tableRows.length} of {(aggregate ? aggregatedRows : data).length} row{(aggregate ? aggregatedRows : data).length !== 1 ? "s" : ""} by Effective Cost — use Export CSV for the full set
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── 3. MG Tracker ─────────────────────────────────────────────────────────────
function MgTracker({ tabId, exportRegistry, metricView }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    setLoading(true);
    apiClient.get("/api/insights/mg")
      .then(res => { setData(res.data); setError(null); })
      .catch(() => setError("Failed to load MG tracker."))
      .finally(() => setLoading(false));
  }, []);

  const actualCostOf    = row => metricValue(row, metricView, "actual_cost_payment_ccy", "actual_cost_theo_payment_ccy", "actual_cost_weighted_payment_ccy");
  const effectiveCostOf = row => metricValue(row, metricView, "effective_cost_payment_ccy", "effective_cost_theo_payment_ccy", "effective_cost_weighted_payment_ccy");
  const shortfallOf     = row => metricValue(row, metricView, "mg_shortfall_payment_ccy", "mg_shortfall_theo_payment_ccy", "mg_shortfall_weighted_payment_ccy");
  const coverageOf      = row => metricValue(row, metricView, "mg_coverage_pct", "mg_coverage_theo_pct", "mg_coverage_weighted_pct");
  const appliesOf       = row => metricValue(row, metricView, "mg_applies", "mg_applies_theo", "mg_applies_weighted");

  if (exportRegistry) {
    exportRegistry.current[tabId] = () => {
      const headers = ["Contract","Currency","Period","MG Amount","Actual Cost","Effective Cost","Shortfall","Coverage","MG Applied"];
      const rows = data.map(row => {
        const ps = (row.period_start?.value || row.period_start)?.slice(0, 10);
        const pe = (row.period_end?.value   || row.period_end)?.slice(0, 10);
        const shortfall = shortfallOf(row);
        return [
          row.contract_name, row.payment_currency, `${ps} → ${pe}`,
          fmt(row.minimum_guarantee), fmt(actualCostOf(row)), fmt(effectiveCostOf(row)),
          shortfall ? fmt(shortfall) : "",
          fmtPct(coverageOf(row)), appliesOf(row) ? "Yes" : "No",
        ];
      });
      downloadCsv(`insights-mg-tracker-${metricView}-${todayStr()}.csv`, headers, rows);
    };
  }

  if (loading) return <LoadingSpinner />;
  if (error)   return <ErrorBox message={error} />;
  if (!data.length) return <p className="text-gray-400 text-sm">No MG contracts found.</p>;

  const triggered = data.filter(r => appliesOf(r)).length;

  function coveragePct(pct) {
    const p = Number(pct) || 0;
    const color = p >= 90 ? "#2E7D32" : p >= 50 ? AMBER : "#C62828";
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-gray-100 rounded-full h-2">
          <div className="h-2 rounded-full" style={{ width: `${Math.min(p, 100)}%`, backgroundColor: color }} />
        </div>
        <span className="text-xs font-medium w-12 text-right" style={{ color }}>{fmtPct(p)}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <KpiCard label="Total MG Contracts" value={data.length} />
        <KpiCard label="MG Triggered"       value={triggered} sub="periods where MG applied" />
        <KpiCard label="MG Not Triggered"   value={data.length - triggered} />
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {["Contract","Currency","Period","MG Amount","Actual Cost","Effective Cost","Shortfall","Coverage","MG Applied"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const ps = (row.period_start?.value || row.period_start)?.slice(0, 10);
              const pe = (row.period_end?.value   || row.period_end)?.slice(0, 10);
              return (
                <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                  <td className="px-4 py-2.5 font-medium">{row.contract_name}</td>
                  <td className="px-4 py-2.5">{row.payment_currency}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{ps} → {pe}</td>
                  <td className="px-4 py-2.5">{fmt(row.minimum_guarantee)}</td>
                  <td className="px-4 py-2.5">{fmt(actualCostOf(row))}</td>
                  <td className="px-4 py-2.5">{fmt(effectiveCostOf(row))}</td>
                  <td className="px-4 py-2.5">{shortfallOf(row) ? fmt(shortfallOf(row)) : "—"}</td>
                  <td className="px-4 py-2.5 w-36">{coveragePct(coverageOf(row))}</td>
                  <td className="px-4 py-2.5">
                    {appliesOf(row)
                      ? <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}>Yes</span>
                      : <span className="text-xs text-gray-400">No</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 5. Sport & League ─────────────────────────────────────────────────────────
const SPORT_LEAGUE_TABLE_LIMIT = 100;

function SportLeague({ filters, tabId, exportRegistry, metricView, currency, ratesByMonth }) {
  const [data,          setData]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [sportOptions,  setSportOptions]  = useState([]);
  const [pendingSports, setPendingSports] = useState([]);
  const [appliedSports, setAppliedSports] = useState([]);
  const [leagueOptions,     setLeagueOptions]     = useState([]);
  const [pendingLeaguesStr, setPendingLeaguesStr] = useState("");
  const [appliedLeaguesStr, setAppliedLeaguesStr] = useState("");
  const [aggregate,     setAggregate]     = useState(false);
  const [chartMode,     setChartMode]     = useState("by_sport");

  const tabDirty = JSON.stringify(pendingSports) !== JSON.stringify(appliedSports)
    || pendingLeaguesStr !== appliedLeaguesStr;

  function parseCommaList(str) {
    if (!str) return [];
    return str.split(",").map(s => s.trim()).filter(Boolean);
  }

  // Fetch sport options once on mount
  useEffect(() => {
    apiClient.get("/api/ref/sports")
      .then(res => setSportOptions(res.data))
      .catch(() => {});
  }, []);

  // League suggestions — scoped to whichever sport(s) are currently picked
  // (even pre-Apply) since the full league list can run into the thousands
  // for a real client, and narrowing by sport keeps it usable.
  useEffect(() => {
    const params = pendingSports.length > 0 ? { sports: pendingSports.join(",") } : {};
    apiClient.get("/api/ref/leagues", { params })
      .then(res => setLeagueOptions(res.data))
      .catch(() => {});
  }, [pendingSports]);

  // Fetch data when global filters or applied sports/leagues change. Always
  // fetches monthly-granularity rows — currency conversion must happen per row
  // before summing, so "Aggregated" is computed client-side below.
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.startMonth) params.start_month = filters.startMonth + "-01";
    if (filters.endMonth)   params.end_month   = filters.endMonth   + "-01";
    if (filters.brands?.length === 1) params.brand = filters.brands[0];

    apiClient.get("/api/insights/sports", { params })
      .then(res => {
        let d = res.data;
        // Client-side filtering
        if (filters.brands?.length > 1)  d = d.filter(r => filters.brands.includes(r.brand_label));
        if (appliedSports.length > 0) {
          // Case-insensitive match since output table uses lowercase canonical_sport
          const lowerSports = appliedSports.map(s => s.toLowerCase());
          d = d.filter(r => lowerSports.includes((r.canonical_sport || "").toLowerCase()));
        }
        const appliedLeagues = parseCommaList(appliedLeaguesStr);
        if (appliedLeagues.length > 0) {
          const lowerLeagues = appliedLeagues.map(s => s.toLowerCase());
          d = d.filter(r => lowerLeagues.includes((r.canonical_league || "").toLowerCase()));
        }
        setData(d);
        setError(null);
      })
      .catch(() => setError("Failed to load sport breakdown."))
      .finally(() => setLoading(false));
  }, [filters, appliedSports, appliedLeaguesStr]);

  function handleApply() {
    setAppliedSports(pendingSports);
    setAppliedLeaguesStr(pendingLeaguesStr);
  }
  function handleReset() {
    setPendingSports([]);
    setAppliedSports([]);
    setPendingLeaguesStr("");
    setAppliedLeaguesStr("");
  }

  const symbol = CURRENCY_SYMBOLS[currency] || currency || "£";

  function conv(amount, row) {
    if (!currency) return amount;
    const monthKey = (row.month?.value || row.month)?.slice(0, 7);
    return convertAmount(amount, row.currency_code, monthKey, currency, ratesByMonth);
  }
  // Turnover has no theo/weighted variant in this table — same value in all 3 metric views
  const turnoverOf  = row => conv(row.total_turnover, row);
  const ggrOf       = row => conv(metricValue(row, metricView, "total_ggr", "total_theo_ggr", "total_ggr_weighted"), row);
  const costOf      = row => conv(metricValue(row, metricView, "total_effective_cost", "total_effective_cost_theo", "total_effective_cost_weighted"), row);
  const netGgrOf    = row => conv(metricValue(row, metricView, "total_post_cost_ggr", "total_post_cost_theo_ggr", "total_post_cost_ggr_weighted"), row);
  // Rev Share % and Cost Share % are same-currency ratios — no conversion needed
  const revShareOf  = row => metricValue(row, metricView, "effective_rev_share_pct", "effective_rev_share_theo_pct", "effective_rev_share_weighted_pct");
  const costShareOf = row => metricValue(row, metricView, "cost_share_of_brand_pct", "cost_share_of_brand_theo_pct", "cost_share_of_brand_weighted_pct");

  // Month-by-month trend for a single selected sport (optionally narrowed by
  // league) — `data` is already filtered down to that scope by the fetch
  // effect above, so this just groups what's already loaded by month.
  function buildMonthlyTrend(rows) {
    const byMonth = {};
    rows.forEach(row => {
      const m = (row.month?.value || row.month)?.slice(0, 7);
      if (!byMonth[m]) byMonth[m] = { month: m, turnover: 0, ggr: 0, net: 0 };
      byMonth[m].turnover += turnoverOf(row) || 0;
      byMonth[m].ggr      += ggrOf(row)      || 0;
      byMonth[m].net      += netGgrOf(row)   || 0;
    });
    return Object.values(byMonth)
      .map(g => ({
        ...g,
        grossMargin: g.turnover ? (g.ggr / g.turnover) * 100 : null,
        netMargin:   g.turnover ? (g.net / g.turnover) * 100 : null,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }
  const canShowTrend = appliedSports.length === 1;
  const trendData = canShowTrend ? buildMonthlyTrend(data) : [];

  const bySport = {};
  (data || []).forEach(row => {
    const s = row.canonical_sport;
    if (!bySport[s]) bySport[s] = { sport: s, cost: 0, turnover: 0, ggr: 0 };
    bySport[s].cost     += costOf(row)     || 0;
    bySport[s].turnover += turnoverOf(row) || 0;
    bySport[s].ggr      += ggrOf(row)      || 0;
  });
  const chartData = Object.values(bySport).sort((a, b) => b.cost - a.cost);

  const headers = aggregate
    ? ["Sport","League","Country","Brand","Turnover","GGR","Cost","Net GGR","Rev Share %","Cost Share %"]
    : ["Month","Sport","League","Country","Brand","Turnover","GGR","Cost","Net GGR","Rev Share %","Cost Share %"];

  // Client-side aggregation (brand + sport + league + country) — mirrors the
  // backend's aggregate query but runs after currency conversion, since a
  // pre-summed multi-month total can't be converted correctly afterwards.
  const byGroup = {};
  (data || []).forEach(row => {
    const key = `${row.brand_label}|${row.canonical_sport}|${row.canonical_league}|${row.canonical_event_country}`;
    if (!byGroup[key]) byGroup[key] = {
      brand_label: row.brand_label, canonical_sport: row.canonical_sport,
      canonical_league: row.canonical_league, canonical_event_country: row.canonical_event_country,
      turnover: 0, ggr: 0, cost: 0, net: 0,
    };
    byGroup[key].turnover += turnoverOf(row) || 0;
    byGroup[key].ggr      += ggrOf(row)      || 0;
    byGroup[key].cost     += costOf(row)     || 0;
    byGroup[key].net      += netGgrOf(row)   || 0;
  });
  const groups = Object.values(byGroup);
  // Cost share of brand needs the brand's total cost across every group, computed
  // after conversion — matching the backend's PARTITION BY brand_label window function
  const brandCostTotals = {};
  groups.forEach(g => { brandCostTotals[g.brand_label] = (brandCostTotals[g.brand_label] || 0) + g.cost; });
  const aggregatedRows = groups.map(g => ({
    ...g,
    revShare:  g.ggr ? (g.cost / g.ggr) * 100 : null,
    costShare: brandCostTotals[g.brand_label] ? (g.cost / brandCostTotals[g.brand_label]) * 100 : null,
  }));

  // Sorted by the active view's Rev Share % descending — drives both the display cap and the CSV export
  const sortedRows = aggregate
    ? [...aggregatedRows].sort((a, b) => (b.revShare || 0) - (a.revShare || 0))
    : [...data].sort((a, b) => (revShareOf(b) || 0) - (revShareOf(a) || 0));
  const tableRows = sortedRows.slice(0, SPORT_LEAGUE_TABLE_LIMIT);

  if (exportRegistry) {
    exportRegistry.current[tabId] = () => {
      const rows = aggregate
        ? sortedRows.map(row => [
            row.canonical_sport, row.canonical_league, row.canonical_event_country, row.brand_label,
            fmt(row.turnover), fmt(row.ggr), fmt(row.cost), fmt(row.net),
            fmtPct(row.revShare), fmtPct(row.costShare),
          ])
        : sortedRows.map(row => {
            const cells = [
              row.canonical_sport, row.canonical_league, row.canonical_event_country, row.brand_label,
              fmt(turnoverOf(row)), fmt(ggrOf(row)), fmt(costOf(row)), fmt(netGgrOf(row)),
              fmtPct(revShareOf(row)), fmtPct(costShareOf(row)),
            ];
            const month = (row.month?.value || row.month)?.slice(0, 7);
            return [month, ...cells];
          });
      downloadCsv(`insights-sport-league-${metricView}-${currency}-${todayStr()}.csv`, headers, rows);
    };
  }

  return (
    <div className="space-y-6">
      <TabFilterBar>
        <MultiSelect
          label="Sport"
          options={sportOptions}
          selected={pendingSports}
          onChange={setPendingSports}
          placeholder="All Sports"
        />
        <div className="relative">
          <ChipInput
            label="League"
            value={pendingLeaguesStr}
            onChange={setPendingLeaguesStr}
            options={leagueOptions}
            placeholder="Type to search leagues..."
          />
        </div>
        <div className="flex items-end gap-2">
          <ApplyButton onClick={handleApply} dirty={tabDirty} />
          {(appliedSports.length > 0 || appliedLeaguesStr) && (
            <button onClick={handleReset} className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-700 mt-4">
              Reset
            </button>
          )}
        </div>
      </TabFilterBar>

      {/* Aggregation toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">View:</span>
        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <button
            onClick={() => setAggregate(false)}
            className="px-4 py-1.5 text-sm font-medium transition-colors"
            style={!aggregate ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            Monthly
          </button>
          <button
            onClick={() => setAggregate(true)}
            className="px-4 py-1.5 text-sm font-medium transition-colors"
            style={aggregate ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            Aggregated
          </button>
        </div>
        <span className="text-xs text-gray-400">
          {aggregate ? "Summed across all months in filter (Rev Share % and Cost Share % recomputed from totals)" : "One row per month"}
        </span>
      </div>

      {loading ? <LoadingSpinner /> : error ? <ErrorBox message={error} /> : !data.length ? (
        <p className="text-gray-400 text-sm">No data found.</p>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-sm font-semibold" style={{ color: NAVY }}>
                {chartMode === "by_sport"
                  ? "Total Cost vs GGR by Sport"
                  : chartMode === "ggr_vs_net"
                    ? (canShowTrend ? `GGR vs Net GGR — ${appliedSports[0]} by Month` : "GGR vs Net GGR by Month")
                    : (canShowTrend ? `Gross vs Net Margin % — ${appliedSports[0]} by Month` : "Gross vs Net Margin % by Month")}
              </h3>
              <div className="flex bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setChartMode("by_sport")}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={chartMode === "by_sport" ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
                >
                  By Sport
                </button>
                <button
                  onClick={() => setChartMode("ggr_vs_net")}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={chartMode === "ggr_vs_net" ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
                >
                  GGR vs Net GGR
                </button>
                <button
                  onClick={() => setChartMode("margins")}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={chartMode === "margins" ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
                >
                  Margins
                </button>
              </div>
            </div>

            {chartMode !== "by_sport" && !canShowTrend ? (
              <p className="text-sm text-gray-400 py-16 text-center">
                Select exactly one sport above (via Apply) to see its month-by-month {chartMode === "margins" ? "margin" : "GGR"} trend.
                Optionally narrow further with the League filter.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                {chartMode === "by_sport" ? (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="sport" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${symbol}${fmt(v)}`} />
                    <Tooltip formatter={v => `${symbol}${fmt(v)}`} />
                    <Legend />
                    <Bar dataKey="ggr"  fill={TEAL}  name="GGR"        radius={[4,4,0,0]} />
                    <Bar dataKey="cost" fill={AMBER} name="Total Cost" radius={[4,4,0,0]} />
                  </BarChart>
                ) : chartMode === "ggr_vs_net" ? (
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${symbol}${fmt(v)}`} />
                    <Tooltip formatter={(v, name) => [`${symbol}${fmt(v)}`, name]} />
                    <Legend />
                    <Bar dataKey="ggr" fill={TEAL} name="GGR"     radius={[4,4,0,0]} />
                    <Bar dataKey="net" fill={NAVY} name="Net GGR" radius={[4,4,0,0]} />
                  </BarChart>
                ) : (
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${Number(v).toFixed(0)}%`} />
                    <Tooltip formatter={(v, name) => [`${Number(v).toFixed(2)}%`, name]} />
                    <Legend />
                    <Bar dataKey="grossMargin" fill={TEAL} name="Gross Margin %" radius={[4,4,0,0]} />
                    <Bar dataKey="netMargin"   fill={NAVY} name="Net Margin %"   radius={[4,4,0,0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {headers.map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aggregate
                  ? tableRows.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                        <td className="px-4 py-2.5 font-medium">{row.canonical_sport}</td>
                        <td className="px-4 py-2.5">{row.canonical_league}</td>
                        <td className="px-4 py-2.5">{row.canonical_event_country}</td>
                        <td className="px-4 py-2.5">{row.brand_label}</td>
                        <td className="px-4 py-2.5">{fmt(row.turnover)}</td>
                        <td className="px-4 py-2.5">{fmt(row.ggr)}</td>
                        <td className="px-4 py-2.5">{fmt(row.cost)}</td>
                        <td className="px-4 py-2.5">{fmt(row.net)}</td>
                        <td className="px-4 py-2.5">{fmtPct(row.revShare)}</td>
                        <td className="px-4 py-2.5">{fmtPct(row.costShare)}</td>
                      </tr>
                    ))
                  : tableRows.map((row, i) => {
                      const month = (row.month?.value || row.month)?.slice(0, 7);
                      return (
                        <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                          <td className="px-4 py-2.5">{month}</td>
                          <td className="px-4 py-2.5 font-medium">{row.canonical_sport}</td>
                          <td className="px-4 py-2.5">{row.canonical_league}</td>
                          <td className="px-4 py-2.5">{row.canonical_event_country}</td>
                          <td className="px-4 py-2.5">{row.brand_label}</td>
                          <td className="px-4 py-2.5">{fmt(turnoverOf(row))}</td>
                          <td className="px-4 py-2.5">{fmt(ggrOf(row))}</td>
                          <td className="px-4 py-2.5">{fmt(costOf(row))}</td>
                          <td className="px-4 py-2.5">{fmt(netGgrOf(row))}</td>
                          <td className="px-4 py-2.5">{fmtPct(revShareOf(row))}</td>
                          <td className="px-4 py-2.5">{fmtPct(costShareOf(row))}</td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
            <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
              Showing top {tableRows.length} of {(aggregate ? aggregatedRows : data).length} row{(aggregate ? aggregatedRows : data).length !== 1 ? "s" : ""} by Rev Share % — use Export CSV for the full set
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── 6. Cost Metric Comparison ─────────────────────────────────────────────────
function CostComparison({ filters, tabId, exportRegistry, currency, ratesByMonth }) {
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [aggregate, setAggregate] = useState(false);
  const [chartMode, setChartMode] = useState("vs_theo");

  const [contractOptions,  setContractOptions]  = useState([]);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [appliedContracts, setAppliedContracts] = useState([]);

  const tabDirty = JSON.stringify(pendingContracts) !== JSON.stringify(appliedContracts);

  // Fetch contract name options once on mount
  useEffect(() => {
    apiClient.get("/api/contracts/")
      .then(res => {
        const names = [...new Set(res.data.map(r => r.contract_name))].sort();
        setContractOptions(names);
      })
      .catch(() => {});
  }, []);

  // Always fetches monthly-granularity rows — currency conversion must
  // happen per row before summing, so "Aggregated" is computed client-side below.
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.startMonth) params.start_month = filters.startMonth + "-01";
    if (filters.endMonth)   params.end_month   = filters.endMonth   + "-01";
    if (filters.brands?.length === 1) params.brand = filters.brands[0];

    apiClient.get("/api/insights/cost-comparison", { params })
      .then(res => {
        let d = res.data;
        if (filters.brands?.length > 1) d = d.filter(r => filters.brands.includes(r.brand_label));
        if (appliedContracts.length > 0) d = d.filter(r => appliedContracts.includes(r.contract_name));
        setData(d);
        setError(null);
      })
      .catch(() => setError("Failed to load cost comparison."))
      .finally(() => setLoading(false));
  }, [filters, appliedContracts]);

  function handleApply() { setAppliedContracts(pendingContracts); }
  function handleReset()  { setPendingContracts([]); setAppliedContracts([]); }

  const symbol = CURRENCY_SYMBOLS[currency] || currency || "£";

  function conv(amount, row) {
    if (!currency) return amount;
    const monthKey = (row.month?.value || row.month)?.slice(0, 7);
    return convertAmount(amount, row.currency_code, monthKey, currency, ratesByMonth);
  }
  const ggrPrimaryOf   = row => conv(row.total_rel_ggr, row);
  const ggrTheoOf      = row => conv(row.total_rel_theo_ggr, row);
  const ggrWeightedOf  = row => conv(row.total_rel_ggr_weighted, row);
  const costPrimaryOf  = row => conv(row.total_cost_primary, row);
  const costTheoOf     = row => conv(row.total_cost_theo, row);
  const costWeightedOf = row => conv(row.total_cost_weighted, row);

  const headers = aggregate
    ? ["Contract", "Brand", "Currency", "Primary Cost", "Theo Cost", "Wtd Cost", "Rev Share %", "Theo Rev Share %", "Wtd Rev Share %"]
    : ["Month", "Contract", "Brand", "Currency", "Primary Cost", "Theo Cost", "Wtd Cost", "Rev Share %", "Theo Rev Share %", "Wtd Rev Share %"];

  // Client-side aggregation (contract + brand) — mirrors the backend's
  // aggregate query but runs after currency conversion, since a pre-summed
  // multi-month total can't be converted correctly afterwards. Rev share %
  // is recomputed from the converted cost/GGR sums rather than averaged.
  const byGroup = {};
  (data || []).forEach(row => {
    const key = `${row.contract_name}|${row.brand_label}`;
    if (!byGroup[key]) byGroup[key] = {
      contract_name: row.contract_name, brand_label: row.brand_label,
      ggrPrimary: 0, ggrTheo: 0, ggrWeighted: 0,
      costPrimary: 0, costTheo: 0, costWeighted: 0,
    };
    byGroup[key].ggrPrimary   += ggrPrimaryOf(row)   || 0;
    byGroup[key].ggrTheo      += ggrTheoOf(row)      || 0;
    byGroup[key].ggrWeighted  += ggrWeightedOf(row)  || 0;
    byGroup[key].costPrimary  += costPrimaryOf(row)  || 0;
    byGroup[key].costTheo     += costTheoOf(row)     || 0;
    byGroup[key].costWeighted += costWeightedOf(row) || 0;
  });
  const aggregatedRows = Object.values(byGroup).map(g => ({
    ...g,
    revSharePrimary:  g.ggrPrimary  ? (g.costPrimary  / g.ggrPrimary)  * 100 : null,
    revShareTheo:     g.ggrTheo     ? (g.costTheo     / g.ggrTheo)     * 100 : null,
    revShareWeighted: g.ggrWeighted ? (g.costWeighted / g.ggrWeighted) * 100 : null,
  }));

  const displayRows = aggregate ? aggregatedRows : data;

  // Top 10 contracts by absolute cost difference vs Standard — always over the
  // whole filtered period regardless of the Monthly/Aggregated table toggle,
  // same as the "top N" charts on the other Insights tabs.
  const byContractCost = {};
  (data || []).forEach(row => {
    const k = row.contract_name;
    if (!byContractCost[k]) byContractCost[k] = { contract: k, costPrimary: 0, costTheo: 0, costWeighted: 0 };
    byContractCost[k].costPrimary  += costPrimaryOf(row)  || 0;
    byContractCost[k].costTheo     += costTheoOf(row)     || 0;
    byContractCost[k].costWeighted += costWeightedOf(row) || 0;
  });
  const contractDiffs = Object.values(byContractCost).map(c => ({
    contract: c.contract,
    vsTheo: c.costTheo - c.costPrimary,
    vsWeighted: c.costWeighted - c.costPrimary,
  }));
  const top10VsTheo     = [...contractDiffs].sort((a, b) => Math.abs(b.vsTheo)     - Math.abs(a.vsTheo)).slice(0, 10);
  const top10VsWeighted = [...contractDiffs].sort((a, b) => Math.abs(b.vsWeighted) - Math.abs(a.vsWeighted)).slice(0, 10);

  // Rev Share % across all three variants for one selected contract — sums
  // across any brands it appears under, using the aggregation already built above.
  let selectedContractSummary = null;
  if (appliedContracts.length === 1) {
    const rows = aggregatedRows.filter(r => r.contract_name === appliedContracts[0]);
    if (rows.length) {
      const s = rows.reduce((acc, r) => ({
        ggrPrimary: acc.ggrPrimary + r.ggrPrimary, ggrTheo: acc.ggrTheo + r.ggrTheo, ggrWeighted: acc.ggrWeighted + r.ggrWeighted,
        costPrimary: acc.costPrimary + r.costPrimary, costTheo: acc.costTheo + r.costTheo, costWeighted: acc.costWeighted + r.costWeighted,
      }), { ggrPrimary: 0, ggrTheo: 0, ggrWeighted: 0, costPrimary: 0, costTheo: 0, costWeighted: 0 });
      selectedContractSummary = [
        { variant: "Standard", revShare: s.ggrPrimary  ? (s.costPrimary  / s.ggrPrimary)  * 100 : null },
        { variant: "Theo",     revShare: s.ggrTheo     ? (s.costTheo     / s.ggrTheo)     * 100 : null },
        { variant: "Weighted", revShare: s.ggrWeighted ? (s.costWeighted / s.ggrWeighted) * 100 : null },
      ];
    }
  }

  if (exportRegistry) {
    exportRegistry.current[tabId] = () => {
      const rows = aggregate
        ? aggregatedRows.map(row => [
            row.contract_name, row.brand_label, currency,
            fmt(row.costPrimary), fmt(row.costTheo), fmt(row.costWeighted),
            fmtPct(row.revSharePrimary), fmtPct(row.revShareTheo), fmtPct(row.revShareWeighted),
          ])
        : data.map(row => {
            const month = (row.month?.value || row.month)?.slice(0, 7);
            return [
              month, row.contract_name, row.brand_label, currency,
              fmt(costPrimaryOf(row)), fmt(costTheoOf(row)), fmt(costWeightedOf(row)),
              fmtPct(row.effective_rev_share_primary_pct), fmtPct(row.effective_rev_share_theo_pct), fmtPct(row.effective_rev_share_weighted_pct),
            ];
          });
      downloadCsv(`insights-cost-comparison-${currency}-${todayStr()}.csv`, headers, rows);
    };
  }

  return (
    <div className="space-y-4">
      <TabFilterBar>
        <MultiSelect
          label="Contract"
          options={contractOptions}
          selected={pendingContracts}
          onChange={setPendingContracts}
          placeholder="All Contracts"
        />
        <div className="flex items-end gap-2">
          <ApplyButton onClick={handleApply} dirty={tabDirty} />
          {appliedContracts.length > 0 && (
            <button onClick={handleReset} className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-700 mt-4">
              Reset
            </button>
          )}
        </div>
      </TabFilterBar>

      {/* Aggregation toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">View:</span>
        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <button
            onClick={() => setAggregate(false)}
            className="px-4 py-1.5 text-sm font-medium transition-colors"
            style={!aggregate ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            Monthly
          </button>
          <button
            onClick={() => setAggregate(true)}
            className="px-4 py-1.5 text-sm font-medium transition-colors"
            style={aggregate ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            Aggregated
          </button>
        </div>
        <span className="text-xs text-gray-400">
          {aggregate ? "Summed across all months in filter (Rev Share % based on GGR)" : "One row per month"}
        </span>
      </div>

      {loading ? <LoadingSpinner /> : error ? <ErrorBox message={error} /> : !data.length ? (
        <p className="text-gray-400 text-sm">No data found.</p>
      ) : (
        <>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-sm font-semibold" style={{ color: NAVY }}>
              {chartMode === "vs_theo"
                ? "Top 10 Contracts — Theo vs Standard Cost Difference"
                : chartMode === "vs_weighted"
                  ? "Top 10 Contracts — Weighted vs Standard Cost Difference"
                  : `Rev Share % by Variant${appliedContracts.length === 1 ? ` — ${appliedContracts[0]}` : ""}`}
            </h3>
            <div className="flex bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setChartMode("vs_theo")}
                className="px-3 py-1.5 text-xs font-medium transition-colors"
                style={chartMode === "vs_theo" ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
              >
                Standard vs Theo
              </button>
              <button
                onClick={() => setChartMode("vs_weighted")}
                className="px-3 py-1.5 text-xs font-medium transition-colors"
                style={chartMode === "vs_weighted" ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
              >
                Standard vs Weighted
              </button>
              <button
                onClick={() => setChartMode("contract_rev_share")}
                className="px-3 py-1.5 text-xs font-medium transition-colors"
                style={chartMode === "contract_rev_share" ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
              >
                Contract Rev Share
              </button>
            </div>
          </div>

          {chartMode === "contract_rev_share" && !selectedContractSummary ? (
            <p className="text-sm text-gray-400 py-16 text-center">
              Select exactly one contract above (via Apply) to compare its Rev Share % across Standard, Theo, and Weighted.
            </p>
          ) : chartMode === "contract_rev_share" ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={selectedContractSummary}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="variant" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${Number(v).toFixed(0)}%`} />
                <Tooltip formatter={v => `${Number(v).toFixed(2)}%`} />
                <Bar dataKey="revShare" fill={TEAL} name="Rev Share %" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(300, (chartMode === "vs_theo" ? top10VsTheo : top10VsWeighted).length * 32)}>
              <BarChart data={chartMode === "vs_theo" ? top10VsTheo : top10VsWeighted} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `${symbol}${fmt(v)}`} />
                <YAxis type="category" dataKey="contract" tick={{ fontSize: 10 }} width={190} interval={0} tickFormatter={v => truncateLabel(v)} />
                <Tooltip formatter={v => `${symbol}${fmt(v)}`} />
                <Bar
                  dataKey={chartMode === "vs_theo" ? "vsTheo" : "vsWeighted"}
                  name={chartMode === "vs_theo" ? "Theo − Standard" : "Weighted − Standard"}
                  radius={[0,4,4,0]}
                >
                  {(chartMode === "vs_theo" ? top10VsTheo : top10VsWeighted).map((entry, i) => (
                    <Cell key={i} fill={(chartMode === "vs_theo" ? entry.vsTheo : entry.vsWeighted) >= 0 ? AMBER : TEAL} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                {headers.map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {aggregate
                ? aggregatedRows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                      <td className="px-4 py-2.5 font-medium whitespace-nowrap">{row.contract_name}</td>
                      <td className="px-4 py-2.5">{row.brand_label}</td>
                      <td className="px-4 py-2.5">{currency}</td>
                      <td className="px-4 py-2.5 text-right">{fmt(row.costPrimary)}</td>
                      <td className="px-4 py-2.5 text-right">{fmt(row.costTheo)}</td>
                      <td className="px-4 py-2.5 text-right">{fmt(row.costWeighted)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtPct(row.revSharePrimary)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtPct(row.revShareTheo)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtPct(row.revShareWeighted)}</td>
                    </tr>
                  ))
                : data.map((row, i) => {
                    const month = (row.month?.value || row.month)?.slice(0, 7);
                    return (
                      <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                        <td className="px-4 py-2.5 whitespace-nowrap">{month}</td>
                        <td className="px-4 py-2.5 font-medium whitespace-nowrap">{row.contract_name}</td>
                        <td className="px-4 py-2.5">{row.brand_label}</td>
                        <td className="px-4 py-2.5">{currency || row.currency_code}</td>
                        <td className="px-4 py-2.5 text-right">{fmt(costPrimaryOf(row))}</td>
                        <td className="px-4 py-2.5 text-right">{fmt(costTheoOf(row))}</td>
                        <td className="px-4 py-2.5 text-right">{fmt(costWeightedOf(row))}</td>
                        <td className="px-4 py-2.5 text-right">{fmtPct(row.effective_rev_share_primary_pct)}</td>
                        <td className="px-4 py-2.5 text-right">{fmtPct(row.effective_rev_share_theo_pct)}</td>
                        <td className="px-4 py-2.5 text-right">{fmtPct(row.effective_rev_share_weighted_pct)}</td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
            {displayRows.length} row{displayRows.length !== 1 ? "s" : ""}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
// ── Main Insights page ────────────────────────────────────────────────────────
export default function Insights() {
  const [activeTab,    setActiveTab]    = useState("summary");
  const [visitedTabs,  setVisitedTabs]  = useState(new Set(["summary"]));
  const [filters,      setFilters]      = useState({
    startMonth: "",
    endMonth:   "",
    brands:     [],
  });
  const [metricView,   setMetricView]   = useState("standard");
  const [currency,     setCurrency]     = useState("GBP");
  const [ratesByMonth, setRatesByMonth] = useState({});
  const exportRegistry = useRef({});

  // Tabs whose figures have Standard/Theo/Weighted variants. Cost Comparison
  // already shows all three side by side, so it's excluded from this filter.
  const METRIC_VIEW_TABS = new Set(["summary", "providers", "mg", "sports"]);

  // Tabs with currency-aware conversion wired up. MG Tracker's MG figures are
  // per contract-period (can span many months) rather than per month, so
  // there's no single clean month to price the conversion off — excluded.
  const CURRENCY_TABS = new Set(["summary", "providers", "sports", "cost-comparison"]);

  // Fetch monthly FX rates once per date-range change — shared by every
  // currency-aware tab, converted client-side per row before any summing.
  useEffect(() => {
    const params = {};
    if (filters.startMonth) params.start_month = filters.startMonth + "-01";
    if (filters.endMonth)   params.end_month   = filters.endMonth   + "-01";
    apiClient.get("/api/ref/fx-rates", { params })
      .then(res => setRatesByMonth(buildRatesByMonth(res.data)))
      .catch(() => {});
  }, [filters.startMonth, filters.endMonth]);

  function handleTabClick(tabId) {
    setActiveTab(tabId);
    setVisitedTabs(prev => new Set([...prev, tabId]));
  }

  function handleExport() {
    const exporter = exportRegistry.current[activeTab];
    if (exporter) exporter();
  }

  const TAB_COMPONENTS = {
    summary:   MonthlySummary,
    providers: ProviderCosts,
    mg:        MgTracker,
    sports:    SportLeague,
    "cost-comparison": CostComparison,
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Insights</h1>
        <p className="text-sm text-gray-500 mt-1">Provider cost analysis across all contracts and brands</p>
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        onExport={handleExport}
        metricView={metricView}
        onMetricViewChange={setMetricView}
        metricViewDisabled={!METRIC_VIEW_TABS.has(activeTab)}
        currency={currency}
        onCurrencyChange={setCurrency}
        currencyDisabled={!CURRENCY_TABS.has(activeTab)}
      />

      <div className="flex gap-1 mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-1 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={activeTab === tab.id
              ? { backgroundColor: TEAL, color: "white" }
              : { color: "#6b7280" }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {TABS.map(tab => {
        const Component = TAB_COMPONENTS[tab.id];
        if (!visitedTabs.has(tab.id)) return null;
        return (
          <div key={tab.id} style={{ display: activeTab === tab.id ? "block" : "none" }}>
            <Component
              filters={filters}
              tabId={tab.id}
              exportRegistry={exportRegistry}
              metricView={METRIC_VIEW_TABS.has(tab.id) ? metricView : "standard"}
              currency={CURRENCY_TABS.has(tab.id) ? currency : null}
              ratesByMonth={ratesByMonth}
            />
          </div>
        );
      })}
    </div>
  );
}